import React, { useEffect, useState } from 'react';
import { Table, Button, DOMHelper, Stack, IconButton, Whisper, Tooltip, toaster, Message, Divider, SortType, Input } from 'rsuite';
import { BaseErrorModel, BaseResponseModel, DateFormatOptions, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import PageContent from '@/components/PageContent';
import ImageCell from '@/components/ImageCell';
import FetchingSelect from '@/components/FetchingSelect';
import { Icon } from '@rsuite/icons';
import { MdDelete, MdEdit } from 'react-icons/md';
import {
  RunningClubCaptainProfile,
  RunningClubCaptainProfilePaginationFilterRequest
} from '@/models/running-club-captain-profile.model';
import {
  deleteRunningClubCaptainProfile,
  getPagedRunningClubCaptainProfiles
} from '@/services/running-club-captain-profile.service';
import { getRunningClubsLookup } from '@/services/running-club.service';
import RunningClubCaptainProfileDrawer from './running-club-captain-profile-drawer';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

export interface RunningClubCaptainProfileListProps {
  runningClubId?: number;
}

const RunningClubCaptainProfileList: React.FC<RunningClubCaptainProfileListProps> = ({ runningClubId }) => {
  const [filterReq, setFilterReq] = useState<RunningClubCaptainProfilePaginationFilterRequest>(new RunningClubCaptainProfilePaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<RunningClubCaptainProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RunningClubCaptainProfile | undefined>(undefined);

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: RunningClubCaptainProfilePaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        runningClubId: runningClubId ?? req.runningClubId,
      };
      const result: PagedResponseModel<RunningClubCaptainProfile> = await getPagedRunningClubCaptainProfiles(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({
          ...pre,
          pageNumber: result.pageNumber,
          totalRecords: result.totalRecords,
          pageSize: result.pageSize
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Captain Profiles:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre,
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  const handleOpenDrawer = (item?: RunningClubCaptainProfile) => {
    setSelectedItem(item);
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setSelectedItem(undefined);
    fetchData(filterReq);
  };

  const onDeleteItem = (id: number, title?: string) => {

    const handleYesClick = async () => {
      setLoading(true);
      try {
        const result: BaseResponseModel<null> = await deleteRunningClubCaptainProfile(id);
        if (result.success) {
          toastSuccessMessage(`Captain Profile Removed.`);
          fetchData(filterReq);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in deleting Captain Profile:${errorModel?.errorMessage}`);
      }
      finally {
        setLoading(false);
        toaster.clear();
      }
    };

    const handleNoClick = () => {
      toaster.clear();
    };

    toaster.push(
      <Message type="error" closable>
        <p style={{ marginBottom: 10 }}>Are you sure you want to remove <strong>{title ?? 'this profile'}</strong>?</p>
        <div>
          <Button appearance="primary" color='red' onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={handleNoClick} style={{ marginLeft: 10 }}>No</Button>
        </div>
      </Message>,
      { placement: 'topCenter', duration: 0 }
    );

  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => handleOpenDrawer()}>
          Add Captain Profile
        </Button>

        <Stack spacing={6}>
          {!runningClubId && <FetchingSelect
            label="Running Club"
            fetchData={(text, page) => getRunningClubsLookup(text, page)}
            value={filterReq.runningClubId}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, runningClubId: dataKey }))}
          />}
          <Input
            placeholder="Keyword"
            value={filterReq.keyword ?? ''}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
        rowHeight={80}
      >
        <Column width={110} align="center">
          <HeaderCell>Picture</HeaderCell>
          <ImageCell dataKey="imageUrl" />
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle" sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle">
          <HeaderCell>User</HeaderCell>
          <Cell>
            {(rowData: RunningClubCaptainProfile) => rowData.user
              ? `${rowData.user.username} | ${rowData.user.firstName} ${rowData.user.lastName}`
              : '—'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle" sortable>
          <HeaderCell>Title</HeaderCell>
          <Cell dataKey="title" />
        </Column>
        <Column flexGrow={2} align="left" verticalAlign="middle">
          <HeaderCell>Description</HeaderCell>
          <Cell dataKey="description" />
        </Column>
        <Column width={130} align="center" verticalAlign="middle" sortable>
          <HeaderCell>Display Order</HeaderCell>
          <Cell dataKey="displayOrder" />
        </Column>
        <Column flexGrow={1} align="left" verticalAlign="middle" sortable>
          <HeaderCell>Created</HeaderCell>
          <Cell>
            {(rowData: RunningClubCaptainProfile) => rowData.createdAt ? new Date(rowData.createdAt).toLocaleDateString(undefined, DateFormatOptions) : ''}
          </Cell>
        </Column>
        <Column width={120} align="left" verticalAlign="middle" fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: RunningClubCaptainProfile) => (
              <Stack spacing={16}>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Edit Profile</Tooltip>}>
                  <IconButton circle size="sm" appearance="ghost"
                    onClick={() => handleOpenDrawer(rowData)}
                    icon={<Icon as={MdEdit} />} />
                </Whisper>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Delete Profile</Tooltip>}>
                  <IconButton circle size="sm" color="red" appearance="ghost"
                    onClick={() => onDeleteItem(rowData.id, rowData.name ?? rowData.title)}
                    icon={<Icon as={MdDelete} />} />
                </Whisper>
              </Stack>
            )}
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({ ...pre, pageNumber: dataKey }))}
        onChangeLimit={dataKey => setFilterReq(pre => ({ ...pre, pageSize: dataKey }))}
      />

      <RunningClubCaptainProfileDrawer
        value={selectedItem}
        runningClubId={runningClubId}
        open={openDrawer}
        onClose={handleCloseDrawer}
      />
    </PageContent>
  );
};

export default RunningClubCaptainProfileList;
